import React, { useState } from 'react';
import { Phone, MessageCircle, Clock, Send, CheckCircle, ArrowRight } from 'lucide-react';
import { contactInfo, services } from '../data/mock';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ScrollReveal } from './ui/scroll-reveal';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData(prev => ({ ...prev, service: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    await new Promise(resolve => setTimeout(resolve, 1500));
    console.log('Form submitted:', formData);
    setIsSubmitting(false);
    setIsSubmitted(true);
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({ name: '', email: '', phone: '', service: '', message: '' });
    }, 4000);
  };

  return (
    <section id="contact" className="py-24">
      <div className="container">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Left Side - Info */}
            <div>
              <ScrollReveal animation="fadeUp">
                <span 
                  className="inline-block text-xs font-semibold uppercase tracking-widest mb-4"
                  style={{ color: 'var(--accent-blue)' }}
                >
                  Get in Touch
                </span>
                <h2 
                  className="text-4xl md:text-5xl font-semibold mb-6 leading-tight"
                  style={{ color: 'var(--text-primary)' }}
                >
                  Ready for a<br />
                  <span className="gradient-text">Spotless Home?</span>
                </h2>
                <p 
                  className="text-lg mb-10 leading-relaxed"
                  style={{ color: 'var(--text-muted)' }}
                >
                  Get your free quote today. We respond within 2 hours during business hours.
                </p>
              </ScrollReveal>

              {/* Contact Options */}
              <div className="space-y-4">
                <ScrollReveal animation="fadeUp" delay={100}>
                  <a 
                    href={`tel:${contactInfo.phone.replace(/[^0-9]/g, '')}`}
                    className="flex items-center gap-5 p-5 rounded-2xl transition-all duration-300 hover:shadow-lg hover:-translate-y-1 group"
                    style={{ 
                      background: 'var(--bg-card)',
                      border: '1px solid var(--border-light)'
                    }}
                  >
                    <div 
                      className="w-14 h-14 rounded-xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110"
                      style={{ background: 'var(--accent-blue)' }}
                    >
                      <Phone size={22} color="white" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold mb-0.5" style={{ color: 'var(--text-primary)' }}>
                        Call Us Directly
                      </p>
                      <p className="text-lg font-medium" style={{ color: 'var(--accent-blue)' }}>
                        {contactInfo.phone}
                      </p>
                    </div>
                    <ArrowRight 
                      size={20} 
                      className="opacity-0 group-hover:opacity-100 transition-all duration-300 -translate-x-2 group-hover:translate-x-0"
                      style={{ color: 'var(--text-muted)' }}
                    />
                  </a>
                </ScrollReveal>

                <ScrollReveal animation="fadeUp" delay={200}>
                  <a 
                    href={`https://wa.me/1${contactInfo.phone.replace(/[^0-9]/g, '')}?text=${encodeURIComponent("Hello! I'd like to request a free quote for cleaning services.")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-5 p-5 rounded-2xl transition-all duration-300 hover:shadow-lg hover:-translate-y-1 group"
                    style={{ 
                      background: 'var(--bg-card)',
                      border: '1px solid var(--border-light)'
                    }}
                  >
                    <div 
                      className="w-14 h-14 rounded-xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110"
                      style={{ background: '#25D366' }}
                    >
                      <svg width={22} height={22} viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold mb-0.5" style={{ color: 'var(--text-primary)' }}>
                        WhatsApp
                      </p>
                      <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                        Quick response guaranteed
                      </p>
                    </div>
                    <ArrowRight 
                      size={20} 
                      className="opacity-0 group-hover:opacity-100 transition-all duration-300 -translate-x-2 group-hover:translate-x-0"
                      style={{ color: 'var(--text-muted)' }}
                    />
                  </a>
                </ScrollReveal>

                <ScrollReveal animation="fadeUp" delay={300}>
                  <div 
                    className="flex items-center gap-5 p-5 rounded-2xl"
                    style={{ 
                      background: 'var(--bg-card)',
                      border: '1px solid var(--border-light)'
                    }}
                  >
                    <div 
                      className="w-14 h-14 rounded-xl flex items-center justify-center"
                      style={{ background: 'var(--accent-gold)' }}
                    >
                      <Clock size={22} color="white" />
                    </div>
                    <div>
                      <p className="font-semibold mb-0.5" style={{ color: 'var(--text-primary)' }}>
                        Business Hours
                      </p>
                      <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                        {contactInfo.hours}
                      </p>
                      <p className="text-sm font-medium mt-1" style={{ color: 'var(--accent-gold)' }}>
                        Emergency: 24/7 Available
                      </p>
                    </div>
                  </div>
                </ScrollReveal>
              </div>
            </div>

            {/* Right Side - Form */}
            <ScrollReveal animation="fadeUp" delay={200}>
              <div 
                className="p-8 md:p-10 rounded-3xl"
                style={{ 
                  background: 'var(--bg-card)',
                  border: '1px solid var(--border-light)',
                  boxShadow: '0 25px 60px rgba(26, 62, 92, 0.08)'
                }}
              >
                {isSubmitted ? (
                  <div className="text-center py-16">
                    <div 
                      className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
                      style={{ background: 'var(--accent-green-light)' }}
                    >
                      <CheckCircle size={40} style={{ color: 'var(--accent-green)' }} />
                    </div>
                    <h3 className="text-2xl font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                      Message Sent!
                    </h3>
                    <p style={{ color: 'var(--text-muted)' }}>
                      We'll get back to you shortly.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label 
                          className="block text-sm font-medium mb-2" 
                          style={{ color: 'var(--text-primary)' }}
                        >
                          Name
                        </label>
                        <Input 
                          type="text" 
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Your name"
                          required
                          className="h-12 rounded-xl border-0"
                          style={{ background: 'var(--bg-section)' }}
                        />
                      </div>
                      <div>
                        <label 
                          className="block text-sm font-medium mb-2" 
                          style={{ color: 'var(--text-primary)' }}
                        >
                          Phone
                        </label>
                        <Input 
                          type="tel" 
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="(619) 000-0000"
                          required
                          className="h-12 rounded-xl border-0"
                          style={{ background: 'var(--bg-section)' }}
                        />
                      </div>
                    </div>

                    <div>
                      <label 
                        className="block text-sm font-medium mb-2" 
                        style={{ color: 'var(--text-primary)' }}
                      >
                        Email
                      </label>
                      <Input 
                        type="email" 
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="your@email.com"
                        className="h-12 rounded-xl border-0"
                        style={{ background: 'var(--bg-section)' }}
                      />
                    </div>

                    <div>
                      <label 
                        className="block text-sm font-medium mb-2" 
                        style={{ color: 'var(--text-primary)' }}
                      >
                        Service
                      </label>
                      <Select value={formData.service} onValueChange={handleServiceChange}>
                        <SelectTrigger 
                          className="h-12 rounded-xl border-0"
                          style={{ background: 'var(--bg-section)' }}
                        >
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent>
                          {services.map((service) => (
                            <SelectItem key={service.id} value={service.title}>
                              {service.title}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label 
                        className="block text-sm font-medium mb-2" 
                        style={{ color: 'var(--text-primary)' }}
                      >
                        Message
                      </label>
                      <Textarea 
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Tell us about your project..."
                        rows={4}
                        className="rounded-xl border-0 resize-none"
                        style={{ background: 'var(--bg-section)' }}
                      />
                    </div>

                    <Button 
                      type="submit" 
                      disabled={isSubmitting}
                      className="w-full h-14 text-base font-semibold rounded-xl transition-all duration-300 hover:opacity-90"
                      style={{ 
                        background: 'var(--navy-primary)',
                        boxShadow: '0 8px 30px rgba(26, 62, 92, 0.3)'
                      }}
                    >
                      {isSubmitting ? (
                        <span className="flex items-center gap-2">
                          <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                          Sending...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <Send size={18} />
                          Get Free Quote
                        </span>
                      )}
                    </Button>

                    <p className="text-center text-xs" style={{ color: 'var(--text-muted)' }}>
                      By submitting, you agree to receive communications from us.
                    </p>
                  </form>
                )}
              </div>
            </ScrollReveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
